import AppConfig from '~/utils/appconfig';

/**
 * Authentication business logic shared by the Entra setup (~/lib/msal.ts), the
 * broker setup (~/lib/oidc.ts), token acquisition (useAccessTokenProvider) and
 * backend user identification (useBackend).
 *
 * Which provider authenticates the application is decided by app-config.json at
 * runtime, so one build serves every environment.
 */

/**
 * Standard OIDC scopes — the only access-token scopes requested in Keycloak
 * mode. Azure-style resource scopes (`<resource>/.default`) are Entra-specific
 * and rejected by Keycloak with `invalid_scope`.
 */
// `offline_access` is required against Zitadel: its Login v2 sends
// `frame-ancestors 'none'`, so MSAL's hidden-iframe silent flow can never
// complete (it fails with monitor_window_timeout). A refresh token is the
// only way to renew without an iframe.
export const OIDC_SCOPES = ['openid', 'profile', 'email', 'offline_access'];

/**
 * Thrown when authentication is explicitly enabled but its configuration is
 * incomplete or invalid. Callers must fail closed (show a setup error) —
 * never treat this as "authentication disabled".
 */
export class AuthConfigurationError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'AuthConfigurationError';
    }
}

/**
 * True when the application authenticates against the identity broker rather
 * than Azure Entra, i.e. when app-config.json sets either broker value.
 *
 * Either value is taken as the intent to use the broker, so that a
 * configuration with one of them missing reaches {@link getBrokerAuthConfig}
 * and fails closed, rather than reading as no identity provider at all and
 * opening the application without sign-in.
 */
export const isBrokerAuthEnabled = (): boolean =>
    !!AppConfig.oidcAuthority || !!AppConfig.oidcClientId;

/**
 * True when Azure Entra is configured and usable.
 *
 * Entra is the only issuer of Azure resource-audience tokens, so it stays
 * available for the Blob storage path even when the application itself
 * authenticates against the broker. `VITE_MSAL_DISABLED` switches off the
 * Entra stack alone; broker authentication is governed by app-config.json.
 */
export const isEntraAuthEnabled = (): boolean => {
    if (import.meta.env.VITE_MSAL_DISABLED === 'true') {
        return false;
    }
    return !!AppConfig.clientId && !!AppConfig.authority;
};

/**
 * True when the application requires a signed-in user. False means no identity
 * provider is configured at all, and protected routes are open.
 */
export const isAuthenticationEnabled = (): boolean =>
    isBrokerAuthEnabled() || isEntraAuthEnabled();

export interface BrokerAuthConfig {
    authority: string;
    clientId: string;
}

/**
 * The validated broker configuration from app-config.json.
 *
 * @throws {AuthConfigurationError} when the values are missing or malformed. A
 *     configuration that is present but unusable must fail closed rather than
 *     silently run the application without authentication.
 */
export const getBrokerAuthConfig = (): BrokerAuthConfig => {
    const authority: string = AppConfig.oidcAuthority;
    const clientId: string = AppConfig.oidcClientId;
    if (!authority || !clientId) {
        throw new AuthConfigurationError(
            'Broker auth is incomplete: app-config.json must set both ' +
                'oidcAuthority and oidcClientId.'
        );
    }
    try {
        // Parsed for validation only: a malformed authority must fail closed
        // here rather than as an opaque discovery error later.
        new URL(authority);
    } catch {
        throw new AuthConfigurationError(
            `oidcAuthority is not a valid URL: ${authority}`
        );
    }
    return { authority, clientId };
};

/**
 * True when the requested scopes include an Azure resource scope that only
 * Entra can mint, such as `https://storage.azure.com/.default`. Such a request
 * must be served by Entra even when the application is signed in against the
 * broker.
 *
 * The backend's own resource scope is excluded: the backend accepts a
 * broker-issued token, so it does not require Entra.
 *
 * Entra resource scopes use `<resource>/<permission>` syntax; plain OIDC
 * scopes never contain a slash.
 */
export const requiresEntraResourceToken = (
    requestedScopes: string[],
    backendClientId: string
): boolean => {
    const backendScope = `${backendClientId}/.default`;
    return requestedScopes.some(
        (scope) => scope.includes('/') && scope !== backendScope
    );
};

let signingOut = false;

/**
 * Whether a sign-out has begun and the browser has not yet left for the
 * provider's end session endpoint.
 *
 * Both providers discard the local session before they navigate, so for that
 * moment the application renders as signed out while still on a guarded route.
 * The guard sends it to the login page, which starts a sign-in on sight — and
 * the provider's own session, which the sign-out has not reached yet, grants
 * that sign-in silently and cancels the sign-out. Consulting this keeps the
 * login page from starting one.
 */
export const isSigningOut = (): boolean => signingOut;

/** Records that a sign-out has begun. Call before navigating to the provider. */
export const signOutStarted = (): void => {
    signingOut = true;
};

/**
 * Records that a sign-out never reached the provider, so the application is
 * still signed in and may sign in again.
 */
export const signOutFailed = (): void => {
    signingOut = false;
};

let loginFailed = false;

/**
 * Whether the provider sent the browser back from a sign-in that did not
 * complete.
 *
 * The login page otherwise starts a sign-in on sight, and a refusal that is the
 * same every time — a misconfigured client, consent that was never granted —
 * would bounce the browser between the application and the provider forever.
 * Consulting this lets the page ask before trying again.
 */
export const hasLoginRedirectFailed = (): boolean => loginFailed;

/** Records that a sign-in came back without completing. */
export const loginRedirectFailed = (): void => {
    loginFailed = true;
};

/** Clears the record, before the user asks for another attempt. */
export const clearLoginRedirectFailure = (): void => {
    loginFailed = false;
};
